import React from 'react';
import { Box, Typography, Modal, Button } from '@lucid/lego';
import styles from './ConfirmationModal.module.scss';

type ConfirmationModalProps = {
  /** Test ID for the modal */
  dataTestId: string;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal message/description */
  message: React.ReactNode;
  /** Theme for the modal */
  theme?: 'light' | 'dark';
  /** Label for the cancel/stay button */
  cancelLabel?: string;
  /** Label for the confirm/continue button */
  confirmLabel?: string;
  /** Handler for cancel button click */
  onCancel?: () => void;
  /** Handler for confirm button click */
  onConfirm: () => void;
};

/**
 * Reusable confirmation modal component
 * @component
 * @param props - Component props
 * @param props.dataTestId - Test ID for the modal
 * @param props.isOpen - Whether the modal is open
 * @param props.onClose - Handler for closing the modal
 * @param props.title - Modal title
 * @param props.message - Modal message/description
 * @param props.theme - Theme for the modal
 * @param props.cancelLabel - Label for the cancel button
 * @param props.confirmLabel - Label for the confirm button
 * @param props.onCancel - Handler for cancel button click
 * @param props.onConfirm - Handler for confirm button click
 * @returns JSX.Element
 * @example
 * ```tsx
 * <ConfirmationModal
 *   dataTestId="unsaved-changes-modal"
 *   isOpen={isModalOpen}
 *   onClose={handleClose}
 *   title="Save Changes"
 *   message="It looks like you have unsaved changes."
 *   cancelLabel="STAY ON THIS PAGE"
 *   confirmLabel="CONTINUE"
 *   onCancel={handleStay}
 *   onConfirm={handleContinue}
 * />
 * ```
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  dataTestId,
  isOpen,
  onClose,
  title,
  message,
  theme = 'light',
  cancelLabel = 'CANCEL',
  confirmLabel = 'CONFIRM',
  onCancel,
  onConfirm,
}) => {
  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  return (
    <Modal
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      theme={theme}
      size="small"
      footer={
        <Box className={styles.modalFooter}>
          <Button
            label={cancelLabel}
            type="secondary"
            color="neutral"
            theme={theme}
            size="medium"
            handleClick={handleCancel}
            dataTestId={`${dataTestId}-cancel-button`}
            gaComponent=""
            isFluid={true}
          />
          <Button
            label={confirmLabel}
            type="primary"
            color="neutral"
            theme={theme}
            size="medium"
            handleClick={onConfirm}
            dataTestId={`${dataTestId}-confirm-button`}
            gaComponent=""
            isFluid={true}
          />
        </Box>
      }
    >
      <Typography align="center" variant="body" size="medium" theme={theme} color="gray-4">
        {message}
      </Typography>
    </Modal>
  );
};

ConfirmationModal.displayName = 'ConfirmationModal';

export default ConfirmationModal;
